import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronLeft, ChevronRight, LogOut, Dumbbell, Target, Wind, Snowflake, Sun, Calendar, PlayCircle } from "lucide-react";

const HYBRID_COLOR = { A: "#D90429", B: "#F59E0B", C: "#22D3EE", D: "#16A34A" };
const HYBRID_NAME = { A: "Long Toss with Pull-Downs", B: "Long Toss — No Pull-Downs", C: "Range of Motion & Light Toss", D: "Range of Motion Only" };

function Section({ icon: Icon, kicker, title, color, children, testid }) {
  return (
    <section className="rounded-xl ink-card overflow-hidden" data-testid={testid}>
      <header className="px-5 py-4 border-b border-[var(--ink-border)] flex items-center gap-3">
        <Icon className="h-5 w-5" style={{ color: color || "var(--brand-secondary)" }} />
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">{kicker}</div>
          <div className="font-display font-bold text-base text-white">{title}</div>
        </div>
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function CheckRow({ id, label, checked, onChange, sub }) {
  return (
    <label className="flex items-start gap-3 py-2 cursor-pointer hover:bg-white/[0.02] rounded px-2 -mx-2" htmlFor={id}>
      <Checkbox id={id} checked={checked} onCheckedChange={onChange} className="mt-1" data-testid={`check-${id}`} />
      <div className="flex-1">
        <div className={`text-sm ${checked ? "text-white/50 line-through" : "text-white"}`}>{label}</div>
        {sub && <div className="text-xs text-white/40 mt-0.5">{sub}</div>}
      </div>
    </label>
  );
}

export default function PlayerTodaysPlan() {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [warmup, setWarmup] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [checks, setChecks] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/players/me/profile"),
      api.get("/pre-throwing-routine"),
    ]).then(([p, w]) => {
      setProfile(p.data);
      setWarmup(w.data);
      const letter = p.data?.readiness?.suggested_hybrid;
      if (letter) api.get(`/recovery-routine/${letter}`).then((r) => setRecovery(r.data));
    }).catch((err) => { console.error("[todays-plan] load failed:", err); });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const tick = (k) => setChecks((c) => ({ ...c, [k]: !c[k] }));
  const handleLogout = async () => { await logout(); nav("/", { replace: true }); };

  if (!profile) return <div className="text-white/60 p-8">Loading today's plan…</div>;

  const sug = profile.readiness?.suggested_hybrid || "C";
  const color = HYBRID_COLOR[sug];
  const score = profile.readiness?.score ?? "—";
  const band = profile.readiness?.band ?? "";
  const activeHybrid = warmup?.hybrids?.find((h) => h.letter === sug);
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: "var(--ink-1)" }} data-testid="todays-plan-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/portal" dark />
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white hover:bg-white/5">
            <LogOut className="h-4 w-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 pt-6 space-y-5">
        <Link to="/portal" className="inline-flex items-center text-sm text-white/50 hover:text-white">
          <ChevronLeft className="h-4 w-4" /> Daily log
        </Link>

        {/* Step 1 — Readiness */}
        <Section icon={Sun} kicker="Step 1 · Readiness" title={today} color={color} testid="plan-readiness">
          <div className="flex items-center gap-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full font-display font-black text-3xl" style={{ backgroundColor: color, color: "#fff" }}>{sug}</div>
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.25em] font-bold uppercase" style={{ color }}>Today's Suggested Hybrid</div>
              <div className="font-display font-bold text-xl text-white leading-tight">{HYBRID_NAME[sug]}</div>
              <div className="text-sm text-white/60 mt-1">Readiness <span className="font-mono-num font-bold text-white">{score}</span> · band <span className="capitalize font-bold text-white">{band}</span></div>
            </div>
          </div>
        </Section>

        {/* Step 2 — Warm-Up */}
        <Section icon={Dumbbell} kicker="Step 2 · Warm-Up" title="Pre-Throwing Routine" color={color} testid="plan-warmup">
          {warmup?.warmup_blueprint?.map((area) => (
            <div key={area.area} className="mb-4 last:mb-0">
              <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">{area.area}</div>
              <div className="mt-1">
                {area.blocks.slice(0, 4).map((b, i) => (
                  <CheckRow key={i} id={`w-${area.area}-${i}`} label={b.label} checked={!!checks[`w-${area.area}-${i}`]} onChange={() => tick(`w-${area.area}-${i}`)} sub={b.drills?.join(" · ")} />
                ))}
              </div>
            </div>
          ))}
          <Link to="/portal/routine" className="inline-flex items-center text-sm mt-2" style={{ color }}>
            See full warm-up with videos <ChevronRight className="h-4 w-4" />
          </Link>
        </Section>

        {/* Step 3 — Throwing volume */}
        {activeHybrid && (
          <Section icon={Target} kicker="Step 3 · Throw" title={`Hybrid ${sug} — ${activeHybrid.name}`} color={color} testid="plan-throwing">
            <div className="text-sm text-white/60 mb-2">{activeHybrid.when}</div>
            <ul className="space-y-1">
              {activeHybrid.throwing_volume?.map((line, i) => (
                <li key={i} className="text-sm text-white/85 font-mono-num flex items-baseline gap-2">
                  <span style={{ color }}>▸</span>{line}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Step 4 — Recovery */}
        <Section icon={Snowflake} kicker={`Step 4 · Recovery · ~${recovery?.total_minutes || "—"} min`} title={recovery?.headline || "Loading…"} color={color} testid="plan-recovery">
          {recovery?.blocks?.map((blk, bi) => (
            <div key={bi} className="mb-3 last:mb-0">
              <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">{blk.phase.split(" · ").slice(-1)[0]}</div>
              <div className="mt-1">
                {blk.items.slice(0, 4).map((it, i) => (
                  <CheckRow key={i} id={`r-${bi}-${i}`} label={it.label} checked={!!checks[`r-${bi}-${i}`]} onChange={() => tick(`r-${bi}-${i}`)} sub={it.drills?.join(" · ")} />
                ))}
              </div>
            </div>
          ))}
          <Link to="/portal/recovery" className="inline-flex items-center text-sm mt-2" style={{ color }}>
            See full recovery with videos <ChevronRight className="h-4 w-4" />
          </Link>
        </Section>

        {/* Step 5 — Log it */}
        <Section icon={Calendar} kicker="Step 5 · Close the loop" title="Submit today's log" color={color} testid="plan-log">
          <p className="text-sm text-white/60">Once you've thrown, head back to your daily log and submit the 30-second check-in so tomorrow's plan adapts.</p>
          <Link to="/portal" className="inline-flex items-center justify-center w-full mt-4">
            <Button className="w-full text-white font-bold py-6 rounded-full" style={{ backgroundColor: color }} data-testid="plan-go-log">
              <PlayCircle className="h-5 w-5 mr-2" /> Open my daily log
            </Button>
          </Link>
        </Section>
      </div>
    </div>
  );
}
